import React, { useState } from "react";
import { toast } from "react-toastify";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";

export const TEAM_ERRORS = {
  invalid: "That did not save. Try again.",
  tooMany: "A brokerage page can have up to 20 teams.",
  nameRequired: "Give the team a name.",
  nameTooLong: "Keep the team name under 80 characters.",
  nameDash: "Use a comma or a period instead of a long dash.",
  nameTaken: "Another team already has that name.",
  noMembers: "Add at least one agent to the team.",
  notMember: "Only agents shown on your page can be on a team.",
  twoTeams: "An agent can be on one team at a time.",
};

export const teamErrorOf = (error) => TEAM_ERRORS[error?.fields?.teams] || error?.message || TEAM_ERRORS.invalid;

const toPayload = (teams) => teams.map((team) => ({ name: team.name, memberIds: team.memberIds }));

const TeamForm = ({ initial, agents, takenIds, busy, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || "");
  const [memberIds, setMemberIds] = useState(initial?.memberIds || []);
  const byId = Object.fromEntries(agents.map((agent) => [agent.userId, agent]));
  const options = agents
    .filter((agent) => !memberIds.includes(agent.userId) && !takenIds.has(agent.userId))
    .map((agent) => ({ value: agent.userId, label: agent.name }));
  return (
    <form
      className="brokerage-studio__team-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ name: name.trim(), memberIds });
      }}
    >
      <label className="brokerage-studio__label">
        Team name
        <input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} placeholder="The Evans Group" />
      </label>
      <div className="brokerage-studio__label">
        Agents on this team
        {memberIds.length ? (
          <ul className="brokerage-studio__list brokerage-studio__team-members">
            {memberIds.map((userId) => (
              <li key={userId}>
                <span className="brokerage-studio__name">{byId[userId]?.name || "Agent"}</span>
                <button
                  type="button"
                  className="brokerage-studio__link"
                  aria-label={`Take ${byId[userId]?.name || "this agent"} off the team`}
                  onClick={() => setMemberIds(memberIds.filter((id) => id !== userId))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {options.length ? (
          <SelectDropDown
            id={`team-add-${initial?.id || "new"}`}
            className="brokerage-studio__select"
            classNamePrefix="select"
            placeholder="Add an agent"
            options={options}
            value={null}
            styles={PICKER_STYLES}
            errorClass="d-none"
            onChange={(option) => option && setMemberIds([...memberIds, option.value])}
          />
        ) : (
          <p className="brokerage-studio__hint">Every agent shown on your page is on a team already.</p>
        )}
      </div>
      <div className="brokerage-studio__team-actions">
        <button type="submit" className="btn btn-primary brokerage-studio__button" disabled={busy}>
          {busy ? "Saving..." : "Save team"}
        </button>
        <button type="button" className="brokerage-studio__link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export const TeamsOnPage = ({ data, saveTeams }) => {
  const [editing, setEditing] = useState(null);
  const teams = data.teams || [];
  const hidden = new Set(data.hidden || []);
  const agents = (data.agents || []).filter((agent) => !hidden.has(agent.userId));
  const nameOf = Object.fromEntries((data.agents || []).map((agent) => [agent.userId, agent.name]));
  const membersOf = (team) => team.memberIds.map((id) => nameOf[id] || "Agent").join(", ");

  const save = (next, done) =>
    saveTeams.mutate(toPayload(next), {
      onSuccess: () => {
        setEditing(null);
        if (done) toast.success(done);
      },
      onError: (error) => toast.error(teamErrorOf(error)),
    });

  const takenExcept = (teamId) =>
    new Set(teams.filter((team) => team.id !== teamId).flatMap((team) => team.memberIds));

  return (
    <section className="content-block">
      <div className="title-sec">
        <h4>Teams</h4>
        {editing === null && agents.length ? (
          <a role="button" tabIndex={0} onClick={() => setEditing("new")} onKeyDown={(event) => event.key === "Enter" && setEditing("new")}>
            Add a team
          </a>
        ) : null}
      </div>
      <p className="brokerage-studio__hint">
        Group agents who work together. Each team gets its own section on your brokerage page, and each member&apos;s bio
        names the team. An agent can be on one&nbsp;team.
      </p>
      {teams.length ? (
        <ul className="brokerage-studio__list brokerage-studio__teams">
          {teams.map((team) =>
            editing === team.id ? (
              <li key={team.id} className="is-editing">
                <TeamForm
                  initial={team}
                  agents={agents}
                  takenIds={takenExcept(team.id)}
                  busy={saveTeams.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(next) => save(teams.map((item) => (item.id === team.id ? next : item)), "Team saved.")}
                />
              </li>
            ) : (
              <li key={team.id}>
                <span className="brokerage-studio__name">
                  {team.name}
                  <span title={membersOf(team)}>{membersOf(team)}</span>
                </span>
                <span className="brokerage-studio__row-tools">
                  <button type="button" className="brokerage-studio__link" disabled={saveTeams.isPending} onClick={() => setEditing(team.id)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="brokerage-studio__link"
                    aria-label={`Remove ${team.name}`}
                    disabled={saveTeams.isPending}
                    onClick={() => save(teams.filter((item) => item.id !== team.id), "Team removed.")}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ),
          )}
        </ul>
      ) : editing !== "new" ? (
        <p className="brokerage-studio__empty">No teams yet. Agents show on your brokerage page in your agent&nbsp;order.</p>
      ) : null}
      {editing === "new" ? (
        <TeamForm
          agents={agents}
          takenIds={takenExcept(null)}
          busy={saveTeams.isPending}
          onCancel={() => setEditing(null)}
          onSave={(next) => save([...teams, next], "Team added.")}
        />
      ) : null}
    </section>
  );
};
